// The glyph an artifact wears in the Files panel (Matt, 10 Sep call: "can we
// think about the icons that pop up for artifacts? we've just seen two tables
// — this should be two tables and two composite tables"). Every table, chart
// and composite the agent saves is a `.json` under data/, so a file-type icon
// shows them all as the same JSON document. The kind is read from what the
// plugins register instead: the artifact type first, then the description and
// filename the render_table / render_chart / compose_table tools write.
import React from 'react'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import WebOutlinedIcon from '@mui/icons-material/WebOutlined'
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined'
import { FileIcon } from '@untitledui/file-icons'
import type { ArtifactInfo } from '../types.js'

export type ArtifactKind = 'table' | 'chart' | 'composite' | 'image' | 'webapp' | 'code' | 'report' | 'data' | 'file'

/** What an artifact IS, for the glyph. Pure, so the rules can be pinned. */
export function artifactKind(artifact: Pick<ArtifactInfo, 'artifactType' | 'fileName' | 'label' | 'description'>): ArtifactKind {
  const t = artifact.artifactType
  if (t === 'image') return 'image'
  if (t === 'webapp') return 'webapp'
  if (t === 'code') return 'code'
  if (t === 'report') return 'report'
  if (t === 'chart') return 'chart'
  const desc = (artifact.description || '').toLowerCase()
  const name = (artifact.fileName || '').toLowerCase()
  const label = (artifact.label || '').toLowerCase()
  // compose_table: "Composite table with per-cell provenance", label "… (composite)".
  if (desc.startsWith('composite') || label.endsWith('(composite)')) return 'composite'
  // render_chart: "Chart data (PlatinumData JSON)", file chart_<top>_x_<side>_<ts>.json.
  if (desc.startsWith('chart data') || name.startsWith('chart_')) return 'chart'
  // render_table: "Table data (PlatinumData JSON)", file table_<top>_x_<side>_<ts>.json.
  if (desc.startsWith('table data') || name.startsWith('table_')) return 'table'
  if (t === 'csv' || t === 'data') return 'data'
  return 'file'
}

/** One colour per kind, quiet enough to sit in a list. */
const KIND_COLOUR: Record<Exclude<ArtifactKind, 'file'>, string> = {
  table: '#2563eb',
  chart: '#7c3aed',
  composite: '#0d9488',
  image: '#db2777',
  webapp: '#ea580c',
  code: '#475569',
  report: '#b45309',
  data: '#6b7280',
}

const KIND_ICON: Record<Exclude<ArtifactKind, 'file'>, React.ElementType> = {
  table: TableChartOutlinedIcon,
  chart: BarChartOutlinedIcon,
  composite: GridOnOutlinedIcon,
  image: ImageOutlinedIcon,
  webapp: WebOutlinedIcon,
  code: CodeOutlinedIcon,
  report: ArticleOutlinedIcon,
  data: DataObjectOutlinedIcon,
}

export function ArtifactGlyph({ artifact, size = 15 }: { artifact: ArtifactInfo; size?: number }) {
  const kind = artifactKind(artifact)
  if (kind === 'file') {
    // A deck, a document, a PDF: the file-type icon says it best.
    const ext = (artifact.fileName || '').split('.').pop()?.toLowerCase() || ''
    return <FileIcon type={ext || 'document'} size={size} />
  }
  const Icon = KIND_ICON[kind]
  return <Icon data-testid={`artifact-glyph-${kind}`} sx={{ fontSize: size + 1, color: KIND_COLOUR[kind], flexShrink: 0 }} />
}
